import { format } from 'date-fns'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { match, P } from 'ts-pattern'

import Button from '@/components-latest/primitives/Button'
import Text from '@/components-latest/primitives/Text'
import { asyncModal } from '@/components-latest/shared/Modals/CommonModal'
import ConfirmationModal from '@/components-latest/shared/Modals/ConfirmationModal'
import CancelOrderModal from '@/components-latest/ui/OrdersPage/CancelOrderModal'
import { IError, isErrorType } from '@/config/http/http.helpers'
import { removeOrderIdFromPending } from '@/ducks/app/slices/app.slice'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import {
  useAcceptOrderMutation,
  useLazyGetOrdersCountByTypesQuery,
  useRejectOrderMutation,
  useReturnToStockOrderMutation,
  useSetOrderReadyMutation
} from '@/ducks/orders/services/orders.api'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'
import { logFirebaseEvent } from '@/utils/firebase.utils'

const asyncInfoModal = async ({
  title,
  description,
  buttonText
}: {
  title: string
  description: string
  buttonText: string
}) =>
  asyncModal.open({
    hideHeader: true,
    withoutCloseIcon: true,
    width: 376,
    body: (
      <div>
        <Text variant="nextTitle-2" gutter={8}>
          {title}
        </Text>
        <Text variant="nextSubtitle">{description}</Text>
      </div>
    ),
    footer: close => (
      <Button variant="primary" size="medium" onClick={close}>
        {buttonText}
      </Button>
    )
  })

interface IOrderActionProps {
  orderId: string
  onRefresh?: () => void
}

const AcceptAction: React.FC<
  IOrderActionProps & { preparationMinutes: number }
> = ({ orderId, preparationMinutes, onRefresh }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [acceptOrder, { isLoading }] = useAcceptOrderMutation()

  const storeId = useSelector(selectStoreId)
  const [updateCount] = useLazyGetOrdersCountByTypesQuery()

  const handleAccept = useCallback(() => {
    return acceptOrder({
      order_id: orderId,
      estimated_preparation_time: preparationMinutes * 60 * 1000
    })
      .unwrap()
      .then(() => {
        logFirebaseEvent('AC_Web_NewOrder_Accept', {
          Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
          Order_ID: orderId,
          Cooking_Time: preparationMinutes,
          Store_ID: storeId
        })
        dispatch(removeOrderIdFromPending({ orderId }))
        updateCount({ store_id: storeId as string })
        dispatch(
          addSystemNotification({
            message: t('orders.notifications.accepted'),
            intent: 'success'
          })
        )
        onRefresh?.()
      })
      .catch((error: IError) => {
        match(error?.status)
          .with(P.union('ORDER_ACCEPTED', 'ALREADY_ACCEPTED'), () =>
            asyncInfoModal({
              title: t('orders.already-accepted-modal.title'),
              description: t('orders.already-accepted-modal.description'),
              buttonText: t('orders.already-accepted-modal.confirm')
            }).then(() => {
              dispatch(removeOrderIdFromPending({ orderId }))
              updateCount({ store_id: storeId as string })
              onRefresh?.()
            })
          )
          .with('ORDER_USED_CANCELLED', () =>
            asyncInfoModal({
              title: t('orders.cancelled-by-client-modal.title'),
              description: t('orders.cancelled-by-client-modal.description'),
              buttonText: t('orders.cancelled-by-client-modal.confirm')
            }).then(() => {
              dispatch(removeOrderIdFromPending({ orderId }))
              updateCount({ store_id: storeId as string })
              onRefresh?.()
            })
          )
          .with('ORDER_ADMIN_CANCELLED', () =>
            asyncInfoModal({
              title: t('orders.cancelled-by-admin-modal.title'),
              description: t('orders.cancelled-by-admin-modal.description'),
              buttonText: t('orders.cancelled-by-admin-modal.confirm')
            }).then(() => {
              dispatch(removeOrderIdFromPending({ orderId }))
              updateCount({ store_id: storeId as string })
              onRefresh?.()
            })
          )
          .with('ALREADY_REJECTED', () =>
            asyncInfoModal({
              title: t('orders.already-rejected-modal.title'),
              description: t('orders.already-rejected-modal.description'),
              buttonText: t('orders.already-rejected-modal.confirm')
            }).then(() => {
              dispatch(removeOrderIdFromPending({ orderId }))
              updateCount({ store_id: storeId as string })
              onRefresh?.()
            })
          )
          .otherwise(() =>
            dispatch(
              addSystemNotification({
                message: isErrorType(error) && error.message
                  ? error.message
                  : isErrorType(error) && error.status
                  ? t(`orders.server.errors.accept.${error.status}`)
                  : t('orders.defaultError', { id: orderId }),
                intent: 'error'
              })
            )
          )
      })
  }, [
    acceptOrder,
    orderId,
    preparationMinutes,
    storeId,
    dispatch,
    updateCount,
    t,
    onRefresh
  ])

  return (
    <>
      <Button
        variant="positive"
        size="medium"
        onClick={handleAccept}
        loading={isLoading}>
        {t('orders.ctas.accept')}
      </Button>
    </>
  )
}

const RejectAction: React.FC<IOrderActionProps> = ({ orderId, onRefresh }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [rejectOrder] = useRejectOrderMutation()

  const storeId = useSelector(selectStoreId)
  const [updateCount] = useLazyGetOrdersCountByTypesQuery()

  const handleReject = useCallback(
    (reason: string) => {
      // Returned so CancelOrderModal can await it and keep itself open when the
      // rejection never reaches the server.
      return rejectOrder({ reason, order_id: orderId })
        .unwrap()
        .then(() => {
          logFirebaseEvent('AC_Web_Reject', {
            Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
            Order_ID: orderId,
            Rejection_Reason: reason,
            Store_ID: storeId
          })
          dispatch(removeOrderIdFromPending({ orderId }))
          updateCount({ store_id: storeId as string })
          dispatch(
            addSystemNotification({
              message: t('orders.notifications.rejected'),
              intent: 'success'
            })
          )
          onRefresh?.()
        })
        .catch((error: IError) => {
          return match(error?.status)
            .with('ALREADY_REJECTED', () =>
              asyncInfoModal({
                title: t('orders.already-rejected-modal.title'),
                description: t('orders.already-rejected-modal.description'),
                buttonText: t('orders.already-rejected-modal.confirm')
              }).then(() => {
                dispatch(removeOrderIdFromPending({ orderId }))
                updateCount({ store_id: storeId as string })
                onRefresh?.()
              })
            )
            .with('ORDER_USED_CANCELLED', () =>
              asyncInfoModal({
                title: t('orders.cancelled-by-client-modal.title'),
                description: t('orders.cancelled-by-client-modal.description'),
                buttonText: t('orders.cancelled-by-client-modal.confirm')
              }).then(() => {
                dispatch(removeOrderIdFromPending({ orderId }))
                updateCount({ store_id: storeId as string })
                onRefresh?.()
              })
            )
            .with('ORDER_ADMIN_CANCELLED', () =>
              asyncInfoModal({
                title: t('orders.cancelled-by-admin-modal.title'),
                description: t('orders.cancelled-by-admin-modal.description'),
                buttonText: t('orders.cancelled-by-admin-modal.confirm')
              }).then(() => {
                dispatch(removeOrderIdFromPending({ orderId }))
                updateCount({ store_id: storeId as string })
                onRefresh?.()
              })
            )
            .otherwise(() => {
              // Not a terminal order state — the rejection simply did not go
              // through. Rethrow so the modal stays open and shows the error
              // instead of closing as if it had succeeded.
              throw {
                ...error,
                message:
                  isErrorType(error) && error.message
                    ? error.message
                    : isErrorType(error) && error.status
                    ? t(`orders.server.errors.accept.${error.status}`)
                    : t('orders.defaultError', { id: orderId })
              } as IError
            })
        })
    },
    [orderId, updateCount, storeId, onRefresh, rejectOrder, t, dispatch]
  )

  return (
    <CancelOrderModal onConfirm={handleReject}>
      <Button variant="destructive" size="medium">
        {t('orders.ctas.reject')}
      </Button>
    </CancelOrderModal>
  )
}

const ReadyAction: React.FC<IOrderActionProps> = ({ orderId, onRefresh }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [setOrderReady, { isLoading }] = useSetOrderReadyMutation()

  const storeId = useSelector(selectStoreId)
  const [updateCount] = useLazyGetOrdersCountByTypesQuery()

  const handleReady = useCallback(() => {
    setOrderReady({ order_id: orderId })
      .unwrap()
      .then(() => {
        updateCount({ store_id: storeId as string })
        dispatch(
          addSystemNotification({
            message: t('orders.notifications.accepted'),
            intent: 'success'
          })
        )
        onRefresh?.()
      })
      .catch((error: IError) =>
        dispatch(
          addSystemNotification({
            message: isErrorType(error)
              ? error.message
              : t('orders.defaultError', { id: orderId }),
            intent: 'error'
          })
        )
      )
  }, [dispatch, onRefresh, orderId, setOrderReady, updateCount, storeId, t])

  return (
    <Button
      variant="primary"
      size="medium"
      onClick={handleReady}
      loading={isLoading}>
      {t('orders.ctas.ready')}
    </Button>
  )
}

const ReturnToStockAction: React.FC<IOrderActionProps> = ({
  orderId,
  onRefresh
}) => {
  const storeId = useSelector(selectStoreId)

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [returnToStock, { isLoading }] = useReturnToStockOrderMutation()

  const [updateCount] = useLazyGetOrdersCountByTypesQuery()

  const handleReturnToStock = useCallback(() => {
    if (!storeId) return

    // Returned so ConfirmationModal awaits the request and keeps itself open
    // when it fails.
    return returnToStock({ order_id: orderId, store_id: storeId })
      .unwrap()
      .then(() => {
        updateCount({ store_id: storeId })

        dispatch(
          addSystemNotification({
            message: 'Returned to stock',
            intent: 'success'
          })
        )
        onRefresh?.()
      })
      .catch((error: IError) => {
        dispatch(
          addSystemNotification({
            message: isErrorType(error)
              ? error.message
              : t('orders.defaultError', { id: orderId }),
            intent: 'error'
          })
        )
        throw error
      })
  }, [dispatch, onRefresh, orderId, returnToStock, storeId, updateCount, t])

  return (
    <ConfirmationModal
      title={t('orders.return-to-stock.title')}
      description={t('orders.return-to-stock.description')}
      onConfirm={handleReturnToStock}>
      <Button loading={isLoading} variant="secondary" size="medium">
        {t('orders.ctas.return-to-stock')}
      </Button>
    </ConfirmationModal>
  )
}

const OrderActions = {
  Accept: AcceptAction,
  Reject: RejectAction,
  Ready: ReadyAction,
  ReturnToStock: ReturnToStockAction
}

export default OrderActions
